import useCollectPaymentMethod from '@/app/components/payment/components/method/payment-method-v2.controller';
import usePaymentController from '@/app/components/payment/payment.controller';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { Box, Image, Text } from '@cmpsr/components';
import { Elements } from '@stripe/react-stripe-js';
import { useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getRoute } from '@/utils';
import { ROUTE_SIGN_UP } from '@/constants/routes';
import { FeatureFlags, useFeatureFlags } from '@/services/feature-flag-provider';
import envService from '@/services/env.service';
import InviteCodeForm from './components/InviteCodeForm';
import MembershipSummary from './components/MembershipSummary';
import PaymentSection from './components/PaymentSection';
import PurchaseSection from './components/PurchaseSection';
import FeatureList from './components/FeatureList';
import ProgressBar from './components/ProgressBar';
import DarkFeaturesSection from './components/DarkFeaturesSection';
import { VARS } from '../common/constants';
import { StripeAmountProvider, useStripeAmount } from './contexts/StripeAmountContext';
import { useStripeAmountUpdater } from './hooks/useStripeAmountUpdater';
import customTheme from '@/constants/theme.custom';

export type PaymentFormState = {
  email: string;
  autoRenewal: boolean;
  coupon: string;
  zip: string;
  inviteCode?: string;
};

export type PaymentFormComponentsProps = {
  form: UseFormReturn<PaymentFormState>;
};

export type InviteCodeFormProps = {
  email: string;
  isEmailEditable: boolean;
  setIsEmailEditable: (isEmailEditable: boolean) => void;
};

export const PaymentForm = () => {
  // Set up the amount updater to bridge events to context
  useStripeAmountUpdater();

  const [darkSectionShouldScroll, setDarkSectionShouldScroll] = useState(false);
  const { isEnabled } = useFeatureFlags();
  const isSinglePaymentSwitch = envService.getEnv().VITE_SWITCH_SINGLE_PAYMENT_ENABLED === 'true';
  const isSinglePaymentFlag = isEnabled(FeatureFlags.IsSinglePaymentEnabled);
  const isSinglePaymentEnabled = isSinglePaymentSwitch || isSinglePaymentFlag;

  const {
    form,
    handleSubmit,
    handleExpressCheckout,
    priceData,
    isCouponVisible,
    showCoupon,
    coupon,
    setCoupon,
    couponValidationError,
    isSubmitting,
    getPrice,
    joinWaitlist,
    isEmailEditable,
    setIsEmailEditable,
    email,
    inviteCode,
    currentPrice,
    isPpraInviteRequired,
    clearedWaitList,
    priceRecurring,
    isTrial,
    isPriceLoading,
  } = useCollectPaymentMethod();

  // State variables for payment section
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [showPaymentError, setShowPaymentError] = useState(false);

  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleBackClick = () => {
    navigate(-1);
  };

  // Redirect to sign up if no email in session
  useEffect(() => {
    if (!email) {
      navigate(`${getRoute(ROUTE_SIGN_UP)}${location.search}`);
    }
  }, [email, navigate, location.search]);

  if (isPpraInviteRequired && !clearedWaitList) {
    return (
      <InviteCodeForm
        email={email}
        isEmailEditable={isEmailEditable}
        setIsEmailEditable={setIsEmailEditable}
        form={form}
        inviteCode={inviteCode}
        joinWaitlist={joinWaitlist}
        isPpraInviteRequired={isPpraInviteRequired}
        handleBackClick={handleBackClick}
      />
    );
  }

  return (
    <Box width="100%" background="white" display="flex" flexDirection="column" paddingBottom={0} position="relative">
      <Box
        position="relative"
        zIndex={2}
        width="100%"
        display="flex"
        flexDirection={{ base: 'column', md: 'row' }}
        minHeight={{ base: 'auto', md: '100vh' }}
      >
        <Box flex={1} display="flex" flexDirection="column" pb={{ base: '40px', md: '40px' }}>
          {/* Logo with beta */}
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            mt={{ base: '20px', md: '60px' }}
            mb={{ base: '20px', md: '30px' }}
            maxWidth={{ base: '330px', md: '330px', lg: '500px', xl: '600px' }}
            mx="auto"
            width="100%"
          >
            <Image
              src="/images/logo-beta.svg"
              alt="Catch Beta"
              w="143px"
              h="auto"
              flexShrink={0}
              display={{ base: 'none', md: 'block' }}
            />
          </Box>

          <Box
            width="100%"
            mb={{ base: '5', md: '8' }}
            maxWidth={{ base: '330px', md: '330px', lg: '500px', xl: '600px' }}
            mx="auto"
          >
            <ProgressBar />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            maxWidth={{ base: '330px', md: '330px', lg: '500px', xl: '600px' }}
            mx="auto"
            width="100%"
            gap="20px"
          >
            <MembershipSummary
              priceData={priceData}
              isCouponVisible={isCouponVisible}
              showCoupon={showCoupon}
              coupon={coupon}
              setCoupon={setCoupon}
              couponValidationError={couponValidationError}
              isSubmitting={isSubmitting}
              getPrice={getPrice}
              isSinglePaymentEnabled={isSinglePaymentEnabled}
              isTrial={isTrial}
              isPriceLoading={isPriceLoading}
              priceRecurring={priceRecurring}
            />

            <FeatureList />

            <PaymentSection
              setIsPaymentComplete={setIsPaymentComplete}
              showPaymentError={showPaymentError}
              setShowPaymentError={setShowPaymentError}
              paymentSectionRef={paymentSectionRef}
              form={form}
              getPrice={getPrice}
              handleSubmit={handleSubmit}
              handleExpressCheckout={handleExpressCheckout}
              priceData={priceData}
              priceRecurring={priceRecurring}
              coupon={coupon}
              setCoupon={setCoupon}
              couponValidationError={couponValidationError}
              isSubmitting={isSubmitting}
              isTrial={isTrial}
            />

            <Box width="100%">
              <PurchaseSection
                form={form}
                isPaymentComplete={isPaymentComplete}
                currentPrice={currentPrice}
                setShowPaymentError={setShowPaymentError}
                paymentSectionRef={paymentSectionRef}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                isTrial={isTrial}
                priceRecurring={priceRecurring}
              />
            </Box>
          </Box>
        </Box>
        <Box
          flex={{ base: 'none', md: 1 }}
          width={{ base: '100%', md: 'auto' }}
          display="block"
          position={{ base: 'relative', md: darkSectionShouldScroll ? 'relative' : 'sticky' }}
          top={{ base: 'auto', md: darkSectionShouldScroll ? 'auto' : '0' }}
          height={{ base: 'auto', md: darkSectionShouldScroll ? 'auto' : '100vh' }}
        >
          <DarkFeaturesSection onContentHeightChange={setDarkSectionShouldScroll} />
        </Box>
      </Box>
    </Box>
  );
};

// Wrapper component that provides Elements context with dynamic amount
const PPRAPaymentPageWithElements = ({ children }: { children: React.ReactNode }) => {
  const { amount } = useStripeAmount();
  const [searchParams] = useSearchParams();
  // Trial is the default; only disable if explicitly set to 'false'
  const isTrial = searchParams.get('trial') !== 'false';

  const { stripePromise, stripeOptions } = usePaymentController({
    variant: 'default',
    amount: amount || VARS.subscription.defaultAmount,
    isTrial,
  });

  return (
    <Elements options={stripeOptions} stripe={stripePromise}>
      {children}
    </Elements>
  );
};

export const PPRAPaymentPage = () => {
  const setIntercomIsVisible = useSetIntercomIsVisible();

  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  return (
    <StripeAmountProvider defaultAmount={VARS.subscription.defaultAmount}>
      <PPRAPaymentPageWithElements>
        <Outlet />
      </PPRAPaymentPageWithElements>
    </StripeAmountProvider>
  );
};
